import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Mail, Pencil, Save, Loader2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { logAdminAction } from '@/lib/auditLog';

const LOCALES = [
  { code: 'en', label: 'English' },
  { code: 'am', label: 'Amharic' },
  { code: 'om', label: 'Oromo' },
];

interface Template {
  id: string;
  key: string;
  locale: string;
  subject: string;
  body_html: string;
  body_text: string;
  variables: string[];
  updated_at: string;
}

// Sensible sample values the preview will substitute for each known variable.
const SAMPLE_VALUES: Record<string, string> = {
  user_name: 'Abebe Kebede',
  full_name: 'Abebe Kebede',
  first_name: 'Abebe',
  recipient_name: 'Abebe Kebede',
  email: 'abebe@example.com',
  listing_title: '3-bedroom villa in Bole',
  listing_url: 'https://ethiorealestates.com/property/sample',
  property_title: '3-bedroom villa in Bole',
  project_name: 'Bole Skyline Tower',
  agent_name: 'Hana Tesfaye',
  agency_name: 'Addis Realty',
  price: 'ETB 8,500,000',
  city: 'Addis Ababa',
  reference_code: 'ET-PR000123',
  reset_link: 'https://ethiorealestates.com/reset-password?token=sample',
  verification_link: 'https://ethiorealestates.com/verify?token=sample',
  message: 'Hi, I am interested in this listing. Please contact me.',
  date: new Date().toLocaleDateString(),
};

function renderTemplate(input: string, vars: string[]): string {
  if (!input) return '';
  return input.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, name) => {
    if (SAMPLE_VALUES[name] !== undefined) return SAMPLE_VALUES[name];
    if (vars?.includes(name)) return `[sample ${name}]`;
    return `{{${name}}}`;
  });
}

export function EmailTemplates() {
  const queryClient = useQueryClient();
  const [activeLocale, setActiveLocale] = useState<'en' | 'am' | 'om'>('en');
  const [editing, setEditing] = useState<Template | null>(null);
  const [draft, setDraft] = useState<{ subject: string; body_html: string; body_text: string }>({
    subject: '', body_html: '', body_text: '',
  });

  const { data: templates, isLoading } = useQuery({
    queryKey: ['email-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('email_templates' as any)
        .select('*')
        .order('key');
      if (error) throw error;
      return (data ?? []) as unknown as Template[];
    },
  });

  const filtered = useMemo(
    () => (templates ?? []).filter((t) => t.locale === activeLocale),
    [templates, activeLocale],
  );

  const openEdit = (t: Template) => {
    setEditing(t);
    setDraft({ subject: t.subject, body_html: t.body_html, body_text: t.body_text });
  };

  const save = useMutation({
    mutationFn: async () => {
      if (!editing) return;
      const { error } = await supabase
        .from('email_templates' as any)
        .update({ ...draft, updated_at: new Date().toISOString() })
        .eq('id', editing.id);
      if (error) throw error;
      await logAdminAction({
        action: 'email_template_save',
        entity_type: 'email_template',
        entity_id: editing.id,
        metadata: { key: editing.key, locale: editing.locale },
      });
    },
    onSuccess: () => {
      toast.success('Template saved');
      setEditing(null);
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
    },
    onError: (e: any) => toast.error(e.message || 'Failed to save template'),
  });

  // Live preview values for the editor
  const previewVars = editing?.variables ?? [];
  const previewSubject = renderTemplate(draft.subject, previewVars);
  const previewHtml = renderTemplate(draft.body_html, previewVars);
  const previewText = renderTemplate(draft.body_text, previewVars);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Mail className="h-6 w-6 text-primary" /> Email Templates
        </h1>
        <p className="text-sm text-muted-foreground">Edit transactional and broadcast email content. Used when SMTP is enabled.</p>
      </div>

      {/* Locale tabs */}
      <div className="flex gap-4 text-sm border-b">
        {LOCALES.map((l) => (
          <button
            key={l.code}
            onClick={() => setActiveLocale(l.code as any)}
            className={`pb-2 transition-colors ${activeLocale === l.code ? 'text-primary border-b-2 border-primary font-semibold' : 'text-muted-foreground hover:text-foreground'}`}
          >
            {l.label}
          </button>
        ))}
      </div>

      <Card className="p-0 overflow-hidden">
        {isLoading ? (
          <div className="p-4 space-y-2">
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-md" />)}
          </div>
        ) : filtered.length === 0 ? (
          <p className="p-12 text-center text-sm text-muted-foreground">No templates for this locale.</p>
        ) : (
          <div className="divide-y">
            {filtered.map((t) => {
              const empty = !t.subject && !t.body_html;
              return (
                <div key={t.id} className="p-4 flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-[260px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <code className="text-xs px-2 py-0.5 rounded bg-muted">{t.key}</code>
                      <Badge variant="outline" className="text-xs uppercase">{t.locale}</Badge>
                      {empty && <Badge variant="secondary" className="text-xs">empty</Badge>}
                    </div>
                    <div className="text-sm font-medium mt-1">{t.subject || <span className="italic text-muted-foreground">No subject</span>}</div>
                    <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{t.body_text || t.body_html || '—'}</div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => openEdit(t)}>
                    <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>
              Edit template — <code className="text-sm">{editing?.key}</code> ({editing?.locale})
            </DialogTitle>
          </DialogHeader>

          {editing && (
            <div className="grid gap-6 md:grid-cols-2">
              {/* Editor column */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Subject</Label>
                  <Input value={draft.subject} onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))} />
                </div>

                <div className="space-y-2">
                  <Label>HTML body</Label>
                  <Textarea
                    value={draft.body_html}
                    onChange={(e) => setDraft((d) => ({ ...d, body_html: e.target.value }))}
                    rows={10}
                    className="font-mono text-xs"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Plain-text fallback</Label>
                  <Textarea
                    value={draft.body_text}
                    onChange={(e) => setDraft((d) => ({ ...d, body_text: e.target.value }))}
                    rows={4}
                    className="font-mono text-xs"
                  />
                </div>

                {editing.variables?.length > 0 && (
                  <div className="rounded-md bg-muted/50 p-3 text-xs">
                    <div className="font-semibold mb-1">Available variables</div>
                    <div className="flex gap-1.5 flex-wrap">
                      {editing.variables.map((v) => (
                        <code key={v} className="px-2 py-0.5 rounded bg-background border">{`{{${v}}}`}</code>
                      ))}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-2">Preview substitutes sample values. Unknown variables show as <code>[sample name]</code>.</p>
                  </div>
                )}
              </div>

              {/* Preview column */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Eye className="h-4 w-4 text-primary" />
                  <Label className="m-0">Live preview</Label>
                </div>

                <Card className="overflow-hidden border-2">
                  <div className="border-b bg-muted/40 p-3 space-y-1">
                    <div className="text-[11px] uppercase text-muted-foreground tracking-wider">Subject</div>
                    <div className="text-sm font-medium break-words">
                      {previewSubject || <span className="italic text-muted-foreground">No subject</span>}
                    </div>
                  </div>

                  <Tabs defaultValue="html" className="w-full">
                    <div className="px-3 pt-2">
                      <TabsList className="h-8">
                        <TabsTrigger value="html" className="text-xs h-6">HTML</TabsTrigger>
                        <TabsTrigger value="text" className="text-xs h-6">Plain text</TabsTrigger>
                      </TabsList>
                    </div>

                    <TabsContent value="html" className="m-0 p-0">
                      <div className="bg-background max-h-[420px] overflow-auto">
                        {previewHtml ? (
                          <div
                            className="prose prose-sm max-w-none p-4 [&_a]:text-primary"
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: previewHtml }}
                          />
                        ) : (
                          <p className="p-6 text-center text-xs text-muted-foreground italic">HTML body is empty</p>
                        )}
                      </div>
                    </TabsContent>

                    <TabsContent value="text" className="m-0 p-0">
                      <div className="bg-background max-h-[420px] overflow-auto p-4">
                        {previewText ? (
                          <pre className="text-xs whitespace-pre-wrap font-sans">{previewText}</pre>
                        ) : (
                          <p className="text-center text-xs text-muted-foreground italic">Plain-text fallback is empty</p>
                        )}
                      </div>
                    </TabsContent>
                  </Tabs>
                </Card>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
